'use client';

import {
  accountantFinderAxiosInstance,
  analyticsAxiosInstance,
  axiosInstance,
  companyAxiosInstance,
  companyCreationAxiosInstance,
  companyInsightsAxiosInstance,
  companyTaxationCalculatorAxiosInstance,
  documentsAxiosInstance,
  knowledgebaseAxiosInstance,
  marketplaceAxiosInstance,
  meetupAxiosInstance,
  precisionServicesAxiosInstance,
  resourceMapAxiosInstance,
  subscriptionsAxiosInstance,
  subscriptionsEngineAxiosInstance,
} from '@bupa-monorepo/shared-frontend/admin/shared';
import { AppRootWrapper } from '@bupa-monorepo/shared-frontend/bupa-components';
import { CoreAppShellV2 } from '@bupa-monorepo/shared-frontend/customer-auth';
import { HubspotProvider } from '@bupa-monorepo/shared-frontend/marketplace-components';
import dayjs from 'dayjs';
import 'dayjs/locale/hu'; // Import Hungarian locale
import { usePathname } from 'next/navigation';
import React, { PropsWithChildren } from 'react';

dayjs.locale('hu');

type Props = Readonly<PropsWithChildren>;

const PUBLIC_PAGES = [
  /^\/dinamikus-kolcsonigenylo$/,
  /^\/dinamikus-kolcsonigenylo\/kolcsonigenylo-lap$/,
  /^\/dinamikus-kolcsonigenylo\/jelenlegi-banki-kapcsolat$/,
  /^\/dinamikus-kolcsonigenylo\/ugyletszereplo-adatai$/,
  /^\/dinamikus-kolcsonigenylo\/ugyletszereplo-adatai\/\d+\/(szemelyes-adatok|ugyfelnyilatkozat|jovedelem-adatok|jovedelem-adatok-2)$/,
  /^\/dinamikus-kolcsonigenylo\/ingatlan-ertekbecsles-megrendelese$/,
  /^\/dinamikus-kolcsonigenylo\/ingatlan-ertekbecsles-megrendelese-2$/,
  /^\/dinamikus-kolcsonigenylo\/ugyfelnyilatkozatok$/,
  /^\/dinamikus-kolcsonigenylo\/ugyfelnyilatkozatok-2$/,
  /^\/dinamikus-kolcsonigenylo\/tajekoztato$/,
  /^\/dinamikus-kolcsonigenylo\/kornyezeti-fenntarthatosagi-ugyfeltajekoztato$/,
  /^\/dinamikus-kolcsonigenylo\/tajekoztatas-adatkezelesrol$/,
  /^\/dinamikus-kolcsonigenylo\/etdr-tajekoztato$/,
  /^\/dinamikus-kolcsonigenylo\/igazolas-hitelkerelem-atvetelrol$/,
  /^\/dinamikus-kolcsonigenylo\/koltsegvetesi-terv-lakasfelujitashoz$/,
  /^\/dinamikus-kolcsonigenylo\/koltsegvetesi-terv-lakasfelujitashoz-2$/,
  /^\/vallalkozas-meghivo$/,
  /^\/regisztracio$/,
  /^\/belepes$/,
  /^\/susi-redirect$/,
];

// A könyvelői csomag letöltő oldala: a pathname-ben ott van a token, ezért
// sem a PUBLIC_PAGES pontos egyezése, sem a skipLayout $-anchorolt mintái nem fogják.
const AccountingPackageDeliveryRoute = /^\/konyveloi-csomag\/[^/]+$/;

// Dinamikus szegmenst tartalmazó publikus oldalak.
const publicPagePatterns = [AccountingPackageDeliveryRoute];

const isPublicPage = (pathname: string) =>
  PUBLIC_PAGES.some((pattern) => pattern.test(pathname)) ||
  publicPagePatterns.some((pattern) => pattern.test(pathname));

const skipLayout = [
  /^\/dinamikus-kolcsonigenylo$/,
  /^\/dinamikus-kolcsonigenylo\/kolcsonigenylo-lap$/,
  /^\/dinamikus-kolcsonigenylo\/jelenlegi-banki-kapcsolat$/,
  /^\/dinamikus-kolcsonigenylo\/ugyletszereplo-adatai$/,
  /^\/dinamikus-kolcsonigenylo\/ugyletszereplo-adatai\/\d+\/szemelyes-adatok$/,
  /^\/dinamikus-kolcsonigenylo\/ugyletszereplo-adatai\/\d+\/ugyfelnyilatkozat$/,
  /^\/dinamikus-kolcsonigenylo\/ugyletszereplo-adatai\/\d+\/jovedelem-adatok$/,
  /^\/dinamikus-kolcsonigenylo\/ugyletszereplo-adatai\/\d+\/jovedelem-adatok-2$/,
  /^\/dinamikus-kolcsonigenylo\/ingatlan-ertekbecsles-megrendelese$/,
  /^\/dinamikus-kolcsonigenylo\/ingatlan-ertekbecsles-megrendelese-2$/,
  /^\/dinamikus-kolcsonigenylo\/ugyfelnyilatkozatok$/,
  /^\/dinamikus-kolcsonigenylo\/ugyfelnyilatkozatok-2$/,
  /^\/dinamikus-kolcsonigenylo\/tajekoztato$/,
  /^\/dinamikus-kolcsonigenylo\/kornyezeti-fenntarthatosagi-ugyfeltajekoztato$/,
  /^\/dinamikus-kolcsonigenylo\/tajekoztatas-adatkezelesrol$/,
  /^\/dinamikus-kolcsonigenylo\/etdr-tajekoztato$/,
  /^\/dinamikus-kolcsonigenylo\/igazolas-hitelkerelem-atvetelrol$/,
  /^\/dinamikus-kolcsonigenylo\/koltsegvetesi-terv-lakasfelujitashoz$/,
  /^\/dinamikus-kolcsonigenylo\/koltsegvetesi-terv-lakasfelujitashoz-2$/,
  /^\/vallalkozas-meghivo$/,
  /^\/vallalkozas-regisztracio$/,
  /^\/accept-invite$/,
  /^\/ujdonsag.*$/,
  /^\/elofizetesek.*$/,
  /^\/iranytu-beallitasok.*$/,
  /^\/vallalkozas-beallitasok\/bankkapcsolatok\/redirect\/?$/,
  /^\/susi-redirect$/,
  /^\/direkt-marketing-nyilatkozat$/,
  /^\/adozasi-asszisztens\/surveys.*/,
  /^\/konyvelo-kereso\/surveys.*/,
  /^\/regisztracio.*/,
  /^\/belepes.*/,
  /^\/vallalkozas-alapitas\/surveys.*/,
  /^\/vallalkozas-alapitas\/egyeni-vallalkozas\/arazas.*/,
  /^\/vallalkozas-alapitas\/egyszemelyes\/arazas.*/,
  /^\/vallalkozas-alapitas\/egyeni-vallalkozas\/tajekozodj.*/,
  /^\/nav-kapcsolat-utmutato$/,
  AccountingPackageDeliveryRoute,
];

export function Body({ children }: Props) {
  const pathname = usePathname();
  const useBareLayout = skipLayout.some((r) => r.test(pathname));

  return (
    <HubspotProvider>
      <AppRootWrapper
        axiosInstances={{
          api: axiosInstance,
          auth: axiosInstance,
          company: companyAxiosInstance,
          resourceMap: resourceMapAxiosInstance,
          documents: documentsAxiosInstance,
          companyTaxationCalculator: companyTaxationCalculatorAxiosInstance,
          analytics: analyticsAxiosInstance,
          accountantFinder: accountantFinderAxiosInstance,
          meetup: meetupAxiosInstance,
          marketplace: marketplaceAxiosInstance,
          companyCreation: companyCreationAxiosInstance,
          companyInsights: companyInsightsAxiosInstance,
          knowledgebase: knowledgebaseAxiosInstance,
          precisionServices: precisionServicesAxiosInstance,
          subscriptions: subscriptionsAxiosInstance,
          subscriptionsEngine: subscriptionsEngineAxiosInstance,
        }}
        protected={!isPublicPage(pathname)}
      >
        {useBareLayout ? (
          <React.Fragment>{children}</React.Fragment>
        ) : (
          <CoreAppShellV2>{children}</CoreAppShellV2>
        )}
      </AppRootWrapper>
    </HubspotProvider>
  );
}
